import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { postGame, uploadGameImage } from "../../../services/gameApi";
import { themeAssetUrl } from "../../../utils/themeAssets";
import "../rules/rules.css";
import "./challenges.css";

function Challenges() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);
  const [words, setWords] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setWords(Array.isArray(data.values_array) ? data.values_array.map((word) => String(word)) : []);
  }, [data]);

  const saveWords = async () => {
    if (!currentTheme) return;
    try {
      setBusy(true);
      await updateThemeData({
        payload: { data: { values_array: words }, currentTheme },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire({ title: "Data Updated" });
    } catch (error) {
      Swal.fire({ title: error.message || "Could not save prompts" });
    } finally {
      setBusy(false);
    }
  };

  const importCsv = async (file) => {
    if (!file || !currentTheme) return;
    try {
      setBusy(true);
      const csv = await file.text();
      await postGame("/importPrompts", adminToken, { csv, currentTheme });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire({ title: "Data Updated" });
    } catch (error) {
      Swal.fire({ title: error.message || "Could not import CSV" });
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (target, file) => {
    if (!file || !currentTheme) return;
    if (!file.name.toLowerCase().endsWith(".png")) {
      Swal.fire({ title: "Image must be a PNG" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({ title: "Filesize exceeds max limit of 5 MB" });
      return;
    }
    const size = await new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(url);
      };
      img.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(url);
      };
      img.src = url;
    });
    if (!size || size.width !== 534 || size.height !== 534) {
      Swal.fire({ title: "Image must be 534×534 PNG" });
      return;
    }
    try {
      setBusy(true);
      await uploadGameImage({ token: adminToken, currentTheme, target, file });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire({ title: "Data Updated" });
    } catch (error) {
      Swal.fire({ title: error.message || "Could not upload image" });
    } finally {
      setBusy(false);
    }
  };

  const showPrompt = Number(data?.show_prompt || 0);
  const ballUrl = themeAssetUrl(data, "ticket");
  const cardUrl = themeAssetUrl(data, "all_icon_image");

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" type="button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="admin-tab-container">
            <div className="admin-tab-title">Challenges</div>
            <div className="challenge-words">
              {words.map((word, index) => (
                <span className="challenge-word" key={`${word}-${index}`}>
                  {word}
                  <button
                    type="button"
                    className="challenge-remove"
                    onClick={() => setWords(words.filter((_, i) => i !== index))}
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="code">
              * Maximum 80 characters per word
              <br />
              * Maximum 20 words — upload a CSV to add, click × to remove
              <br />
              words {words.length} / 20
            </div>
            {showPrompt > words.length ? (
              <div className="challenge-warning">
                No. of questions to display is {showPrompt}, but only {words.length} prompts are saved.
              </div>
            ) : null}
            <div className="rules-action-holder">
              <button className="save-and-continue" type="button" disabled={busy} onClick={saveWords}>
                Save
              </button>
            </div>

            <div className="form-group-v1">
              <label>Upload prompts CSV</label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  importCsv(file);
                }}
              />
              <div className="code">
                * Header must be a single column named words.{" "}
                <a href="/equity-ball-sample.csv" download="equity-ball-sample.csv">Download sample</a>
              </div>
            </div>

            <div className="challenge-upload-row">
              <div>
                <label>Ball image (PNG 534×534)</label>
                <input
                  type="file"
                  accept="image/png"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    uploadImage("ticket", file);
                  }}
                />
                {ballUrl ? <img src={ballUrl} alt="" className="challenge-preview" /> : null}
              </div>
              <div>
                <label>Question card background (PNG 534×534)</label>
                <input
                  type="file"
                  accept="image/png"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    uploadImage("all_icon_image", file);
                  }}
                />
                {cardUrl ? <img src={cardUrl} alt="" className="challenge-preview" /> : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Challenges;
